/**
 * Three-tier conviction selector for prediction quality.
 *
 *   ↑ HIGHER  — "model under-rates this player"
 *   = EQUAL   — "model is roughly right"
 *   ↓ LOWER   — "model over-rates this player"
 *
 * Aggregates feed back into the prediction model as a ±5 % community bias.
 * Each cast vote awards +1 reputation (server-side).
 *
 * Props:
 *   playerId, gwWindow, votes (snapshot from parent — {higher, equal, lower,
 *   total, score, community_bias, my_vote}), onChange (optional callback that
 *   receives the new aggregate after a successful cast).
 */
import React, { useState } from "react";
import { CaretUp, CaretDown, Equals } from "@phosphor-icons/react";
import { toast } from "sonner";
import { castPredictionVote } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const CONVICTIONS = [
    {
        id: "HIGHER", icon: CaretUp, label: "Higher", color: "#9EE8B4",
        copy: "I think the model is under-rating this player. He'll score more than the projection suggests — fixtures, form, or hidden context the model misses. Successful picks earn +5 reputation after the GW resolves.",
    },
    {
        id: "EQUAL", icon: Equals, label: "Equal", color: "#9DB7F5",
        copy: "Model's projection looks about right. Solid pick, no edge either way. Casts +1 rep immediately; +5 if your call lands in the ±15% band of actual points after GW.",
    },
    {
        id: "LOWER", icon: CaretDown, label: "Lower", color: "#FF6B6B",
        copy: "Model is over-rating this player. He'll under-deliver vs. projection — minutes risk, tough hidden fixture, or recent form not yet baked in. +5 rep if his actual is ≤90% of projection.",
    },
];

export default function VoteButtons({
    playerId,
    gwWindow = 3,
    votes,
    onChange,
    compact = false,
    predictedAtCast,
    targetGwStart,
}) {
    const { user, login } = useAuth();
    const [busy, setBusy] = useState(false);
    const [local, setLocal] = useState(votes);
    React.useEffect(() => { setLocal(votes); }, [votes]);

    const counts = local || { higher: 0, equal: 0, lower: 0, total: 0, community_bias: 0, my_vote: null };
    const biasPct = Math.round(((counts.community_bias) || 0) * 1000) / 10; // ±X.X %

    const handleVote = async (conviction) => {
        if (!user) {
            toast.error("Sign in to vote", {
                action: { label: "Sign in", onClick: login },
            });
            return;
        }
        if (busy) return;
        setBusy(true);
        try {
            const payload = { conviction, gw_window: gwWindow };
            if (predictedAtCast != null && Number.isFinite(Number(predictedAtCast))) {
                payload.predicted_at_cast = Number(predictedAtCast);
            }
            if (targetGwStart != null && Number.isFinite(Number(targetGwStart))) {
                payload.target_gw_start = Number(targetGwStart);
            }
            const fresh = await castPredictionVote(playerId, payload);
            setLocal(fresh);
            onChange?.(fresh);
            if (fresh.awarded_reputation) {
                toast.success("+1 reputation · thanks for voting");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Vote failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <TooltipProvider delayDuration={200}>
            <div
                className="flex items-center gap-1"
                data-testid={`vote-buttons-${playerId}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex border border-white/15">
                    {CONVICTIONS.map(({ id, icon: Icon, label, color, copy }) => {
                        const active = counts.my_vote === id;
                        const count = id === "HIGHER" ? counts.higher : id === "EQUAL" ? counts.equal : counts.lower;
                        return (
                            <Tooltip key={id}>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => handleVote(id)}
                                        data-testid={`vote-${playerId}-${id.toLowerCase()}`}
                                        className={`flex items-center gap-1 px-1.5 py-1 transition-colors font-mono text-[10px] tabular-nums cursor-help ${active ? "" : "text-white/55 hover:text-white"}`}
                                        style={active ? { background: color, color: "#050505" } : {}}
                                    >
                                        <Icon size={11} weight="bold" />
                                        {!compact && <span>{count}</span>}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="top"
                                    className="max-w-[260px] bg-black border border-white/20 text-white rounded-none font-mono text-[11px] leading-snug tracking-normal px-3 py-2"
                                >
                                    <div className="font-display font-black uppercase text-[10px] mb-1" style={{ color }}>
                                        {label} · {count} vote{count === 1 ? "" : "s"}
                                    </div>
                                    <div className="text-white/80">{copy}</div>
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </div>
                {!compact && counts.total > 0 && (
                    <span
                        className="font-mono text-[10px] tabular-nums whitespace-nowrap"
                        data-testid={`vote-bias-${playerId}`}
                        style={{
                            color: counts.community_bias > 0 ? "#9EE8B4"
                                : counts.community_bias < 0 ? "#FF6B6B"
                                    : "#A1A1AA",
                        }}
                        title={`${counts.total} vote${counts.total === 1 ? "" : "s"} · model adjusted by ${biasPct >= 0 ? "+" : ""}${biasPct.toFixed(1)}%`}
                    >
                        {counts.community_bias === 0
                            ? `${counts.total}↕`
                            : `${biasPct >= 0 ? "+" : ""}${biasPct.toFixed(1)}%`}
                    </span>
                )}
            </div>
        </TooltipProvider>
    );
}
